import { tenantSlugFromHostname } from "@/lib/tenant";
import type { TenantSlug } from "@/lib/theme-presets";

export interface AuthFrontDoor {
  tenant: TenantSlug;
  transition: "legacy" | "tenant-internal";
  activation: "ready" | "preparing";
  url: string;
  anonKey: string;
}

type AuthEnvironment = Partial<Record<
  | "VITE_LMS_AUTH_MURTADA_URL"
  | "VITE_LMS_AUTH_MURTADA_ANON_KEY"
  | "VITE_LMS_AUTH_MUFID_URL"
  | "VITE_LMS_AUTH_MUFID_ANON_KEY"
  | "VITE_SUPABASE_URL"
  | "VITE_SUPABASE_ANON_KEY"
  | "VITE_LMS_AUTH_MUFID_TRANSITION"
  | "VITE_LMS_AUTH_MURTADA_STAGE"
  | "VITE_RELEASE_ENVIRONMENT",
  string
>>;

interface AuthEnvironmentFields {
  transition: AuthFrontDoor["transition"];
  url: keyof AuthEnvironment;
  anonKey: keyof AuthEnvironment;
}

interface AuthTransitionPolicy {
  environment?: keyof AuthEnvironment;
  compatibilityValue?: AuthFrontDoor["transition"];
  compatibilityFields?: AuthEnvironmentFields;
}

const LEGACY_FIELDS: AuthEnvironmentFields = {
  transition: "legacy",
  url: "VITE_SUPABASE_URL",
  anonKey: "VITE_SUPABASE_ANON_KEY",
};

const FIELDS: Readonly<Record<TenantSlug, AuthEnvironmentFields>> = {
  murtada: {
    transition: "tenant-internal",
    url: "VITE_LMS_AUTH_MURTADA_URL",
    anonKey: "VITE_LMS_AUTH_MURTADA_ANON_KEY",
  },
  mufid: {
    transition: "tenant-internal",
    url: "VITE_LMS_AUTH_MUFID_URL",
    anonKey: "VITE_LMS_AUTH_MUFID_ANON_KEY",
  },
};

type MurtadaAuthStage = "legacy" | "provider-ready" | "internal";

interface MurtadaStagePolicy {
  activations: Readonly<Record<TenantSlug, AuthFrontDoor["activation"]>>;
  frontDoors: Readonly<Record<TenantSlug, AuthEnvironmentFields>>;
}

const MURTADA_STAGE_POLICIES: Readonly<Record<MurtadaAuthStage, MurtadaStagePolicy>> = {
  legacy: {
    activations: {
      murtada: "ready",
      mufid: "ready",
    },
    frontDoors: {
      murtada: LEGACY_FIELDS,
      mufid: FIELDS.mufid,
    },
  },
  "provider-ready": {
    // The dedicated provider is health-checked, but browser activation must
    // remain inert while Murtada deliberately uses the legacy front door.
    activations: {
      murtada: "preparing",
      mufid: "ready",
    },
    frontDoors: {
      murtada: LEGACY_FIELDS,
      mufid: FIELDS.mufid,
    },
  },
  internal: {
    activations: {
      murtada: "ready",
      mufid: "ready",
    },
    frontDoors: FIELDS,
  },
};

function murtadaAuthStage(env: AuthEnvironment): MurtadaAuthStage | null {
  const stage = env.VITE_LMS_AUTH_MURTADA_STAGE?.trim() || "legacy";
  return stage === "legacy" || stage === "provider-ready" || stage === "internal"
    ? stage
    : null;
}

const TRANSITION_POLICIES: Readonly<Record<TenantSlug, AuthTransitionPolicy>> = {
  murtada: {},
  mufid: {
    environment: "VITE_LMS_AUTH_MUFID_TRANSITION",
    compatibilityValue: "legacy",
    compatibilityFields: LEGACY_FIELDS,
  },
};

function validUrl(value: string, allowLocalHttp: boolean): boolean {
  try {
    const url = new URL(value);
    const validProtocol = url.protocol === "https:"
      || (allowLocalHttp && url.protocol === "http:" && ["127.0.0.1", "localhost"].includes(url.hostname));
    return validProtocol && url.username === "" && url.password === ""
      && url.pathname === "/" && url.search === "" && url.hash === "";
  } catch {
    return false;
  }
}

export function authFrontDoorForHostname(
  hostname: string = window.location.hostname,
  env: AuthEnvironment = import.meta.env,
): AuthFrontDoor | null {
  const tenant = tenantSlugFromHostname(hostname);
  if (!tenant) return null;
  const murtadaStage = murtadaAuthStage(env);
  if (!murtadaStage) return null;
  const murtadaPolicy = MURTADA_STAGE_POLICIES[murtadaStage];
  const policy = TRANSITION_POLICIES[tenant];
  const transition = policy.environment ? env[policy.environment]?.trim() : undefined;
  if (transition && transition !== policy.compatibilityValue) return null;
  if (
    murtadaStage === "internal"
    && policy.compatibilityValue !== undefined
    && transition === policy.compatibilityValue
  ) return null;
  const defaultFields = murtadaPolicy.frontDoors[tenant];
  const fields = transition === policy.compatibilityValue
    ? policy.compatibilityFields ?? defaultFields
    : defaultFields;
  const url = env[fields.url]?.trim().replace(/\/+$/, "") ?? "";
  const anonKey = env[fields.anonKey]?.trim() ?? "";
  if (!validUrl(`${url}/`, env.VITE_RELEASE_ENVIRONMENT !== "production") || !anonKey || anonKey.length > 4096) return null;
  return {
    tenant,
    transition: fields.transition,
    activation: murtadaPolicy.activations[tenant],
    url,
    anonKey,
  };
}

export function authStorageKey(frontDoor: AuthFrontDoor): string {
  if (frontDoor.transition === "legacy") return "imam-lms-auth";
  return `imam-lms-auth-${frontDoor.tenant}`;
}
